/**
 * Director-specific API helpers.
 *
 * The three insight endpoints (`risk-band`, `sick-clustering`,
 * `pending-backlog`) are served by the existing team-insights router
 * (`/api/v1/insights/*`) which already accepts Director+ roles via
 * RLS. We re-export typed wrappers here so `DirectorDashboardPage`
 * has a single import point for director data.
 *
 * No new backend endpoints were required — all three already existed
 * and gated correctly for Director.
 */
import {
  getPatternSignals,
  getSickClustering,
  getPendingBacklog,
  type PatternSignalsResponse,
  type SickClusteringResponse,
  type PendingBacklogResponse,
  type SickClusteringQuery,
  type PendingBacklogQuery,
} from './team-insights'

// ── Director risk-band ───────────────────────────────────────────────────────

export type { PatternSignalsResponse }

/**
 * Fetch org-wide risk-band distribution for the Director's decision queue.
 * Delegates to `GET /api/v1/insights/pattern-signals`. Director role is
 * HR+ so the RLS scope covers the full company.
 */
export function fetchRiskBand(): Promise<PatternSignalsResponse> {
  return getPatternSignals()
}

// ── Director sick-clustering ─────────────────────────────────────────────────

export type { SickClusteringResponse, SickClusteringQuery }

/**
 * Fetch org-wide same-day same-team sick-leave clusters.
 * Delegates to `GET /api/v1/insights/sick-clustering`.
 *
 * Director default window is 30 days (wider than the HOD default of 14)
 * so the dashboard captures clusters from the past month at a glance.
 */
export function fetchSickClustering(
  query: SickClusteringQuery = { windowDays: 30, threshold: 3 },
): Promise<SickClusteringResponse> {
  return getSickClustering(query)
}

// ── Director pending-backlog ─────────────────────────────────────────────────

export type { PendingBacklogResponse, PendingBacklogQuery }

/**
 * Fetch per-manager pending-leave backlogs over the SLA threshold.
 * Delegates to `GET /api/v1/insights/pending-backlog`.
 *
 * Director default stale threshold is 5 working days (the task-spec
 * `?stale_days=5` maps to `minBusinessDays=5` in the existing endpoint).
 */
export function fetchPendingBacklog(
  query: PendingBacklogQuery = { minBusinessDays: 5, thresholdUnactioned: 1 },
): Promise<PendingBacklogResponse> {
  return getPendingBacklog(query)
}
